// eslint-disable-next-line jira/restricted/react-component-props
import React, { type ComponentType, type ComponentProps } from 'react';
import noop from 'lodash/noop';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import {
	SpotlightTarget as SpotlightTargetDI,
	// @ts-expect-error - TS2305 - Module '"@atlaskit/onboarding"' has no exported member 'SpotlightTargetProps'.
	type SpotlightTargetProps,
} from '@atlaskit/onboarding';
import { PopupIntegrationDI } from '@atlassian/jira-atlassian-switcher-common-integration-view';
import {
	AtlassianSwitcherIntegration as AtlassianSwitcherIntegrationDI,
	type AtlassianSwitcherIntegrationProps,
} from '@atlassian/jira-atlassian-switcher-integration';
import type { IntegrationViewRendererProps } from '@atlassian/jira-atlassian-switcher-integration/src/types';
import type { Environment } from '@atlassian/jira-shared-types';
import {
	TenantContextSubscriber as TenantContextSubscriberDI,
	useLocale as useLocaleDI,
} from '@atlassian/jira-tenant-context-controller';
import {
	PostOfficeProvider,
	type PostOfficeContextConfig,
	type PostOfficeEnvironmentConfig,
} from '@atlassian/post-office-placement-remote';
import { APP_SWITCHER_SPOTLIGHT_KEY } from './constants';

type Props = {
	isOpen: boolean;
	AtlassianSwitcherIntegration?: ComponentType<AtlassianSwitcherIntegrationProps>;
	IntegrationViewRenderer?: ComponentType<IntegrationViewRendererProps>;
	SpotlightTarget?: ComponentType<SpotlightTargetProps>;
	TenantContextSubscriber?: ComponentType<
		JSX.LibraryManagedAttributes<
			typeof TenantContextSubscriberDI,
			ComponentProps<typeof TenantContextSubscriberDI>
		>
	>;
	useLocale?: typeof useLocaleDI;
	onClose: () => void;
	onOpen: () => void;
};

const DEFAULT_POST_OFFICE_CONTEXT: PostOfficeContextConfig['current'] = {
	product: 'jira',
	locale: 'en',
	orgId: undefined,
	subproduct: undefined,
	tenantId: undefined,
	isSiteAdmin: undefined,
	workspaceId: undefined,
};

const mapJiraEnvtoPostOfficeEnv = (
	environment: Environment | null,
): PostOfficeEnvironmentConfig['env'] => {
	switch (environment) {
		case 'local':
			return 'local';
		case 'prod':
			return 'production';
		case 'staging':
		case 'dev':
			return 'staging';
		default:
			return 'local';
	}
};

export const AppSwitcher = ({
	AtlassianSwitcherIntegration = AtlassianSwitcherIntegrationDI,
	isOpen,
	onClose,
	onOpen,
	IntegrationViewRenderer = PopupIntegrationDI,
	SpotlightTarget = SpotlightTargetDI,
	TenantContextSubscriber = TenantContextSubscriberDI,
	useLocale = useLocaleDI,
}: Props) => {
	const locale = useLocale();

	return (
		<TenantContextSubscriber>
			{({ tenantContext }) => {
				if (!tenantContext) return null;

				const { activationId, cloudId, environment, isSiteAdmin } = tenantContext;

				return (
					<PostOfficeProvider
						context={{
							current: {
								// we check keys individually rather then by parent object reference, so no need to memoize
								...DEFAULT_POST_OFFICE_CONTEXT,
								locale,
								isSiteAdmin,
								tenantId: cloudId,
								workspaceId: activationId,
							},
						}}
						environment={{ env: mapJiraEnvtoPostOfficeEnv(environment) }}
					>
						<SpotlightTarget name={APP_SWITCHER_SPOTLIGHT_KEY}>
							<AtlassianSwitcherIntegration
								isOpen={isOpen}
								onOpen={onOpen}
								onClose={onClose}
								onCloseComplete={noop}
								TenantContextSubscriber={TenantContextSubscriber}
								IntegrationViewRenderer={IntegrationViewRenderer}
							/>
						</SpotlightTarget>
						<EngagementSpotlight engagementId={APP_SWITCHER_SPOTLIGHT_KEY} />
					</PostOfficeProvider>
				);
			}}
		</TenantContextSubscriber>
	);
};

export default AppSwitcher;
